import {getBackendErrorMessage} from "../../shared/utils/httpError";
import {portalDaConta} from "./portalUtils";
export {extractListPayload} from "../../shared/utils/payload";

// Só três tipos (BBF-181). Se a pessoa é colaboradora do cliente, fornecedora, tutora ou
// credenciada, isso fica no sistema que ela usa; o papel na assinatura fica no vínculo.
export const USER_ACCESS_TYPE_OPTIONS = ['ADMIN', 'SUPPORT', 'USER'];

/** ADMIN e SUPPORT são a equipe Busifarm: veem todas as assinaturas e não têm vínculo. */
export const ehEquipeBusifarm = (type) => type === 'ADMIN' || type === 'SUPPORT';

// Vínculo da conta com cada assinatura (BBF-67): papel, situação e validade valem só nela.
export const PAPEL_NO_VINCULO_OPTIONS = [
    {value: 'USUARIO', label: 'Usuário', description: 'Opera com os perfis que recebeu nesta assinatura.'},
    {value: 'ADMINISTRADOR', label: 'Administrador', description: 'Administra a assinatura: só outro Administrador ou a equipe Busifarm concede ou altera este papel.'},
];

export const SITUACAO_DO_VINCULO_OPTIONS = [
    {value: 'ATIVO', label: 'Ativo'},
    {value: 'DESATIVADO', label: 'Desativado'},
    {value: 'BLOQUEADO', label: 'Bloqueado'},
];

const idDaLicenca = (license) => license?.id || license?.licenseId || '';

const normalizeVinculo = (item) => ({
    licenseId: item?.licenseId || item?.licencaId || '',
    papel: item?.papel || 'USUARIO',
    situacao: item?.situacao || 'ATIVO',
    validoAte: item?.validoAte ? String(item.validoAte).slice(0, 10) : '',
    vale: item?.vale !== false,
});

/**
 * Vínculo da licença no formulário. Licença sem vínculo gravado vale como o backend a trata: papel
 * padrão (USUARIO) e ATIVO.
 */
export const vinculoDaLicenca = (vinculos = [], licenseId, papelPadrao = 'USUARIO') => (
    vinculos.find((item) => item.licenseId === licenseId)
    || {licenseId, papel: papelPadrao, situacao: 'ATIVO', validoAte: '', vale: true}
);

export const atualizarVinculo = (vinculos = [], licenseId, mudanca, papelPadrao = 'USUARIO') => {
    const atual = vinculoDaLicenca(vinculos, licenseId, papelPadrao);
    return [...vinculos.filter((item) => item.licenseId !== licenseId), {...atual, ...mudanca, licenseId}];
};

/**
 * Vínculos do formulário no contrato do identity: só os que vieram da leitura (os do escopo de quem
 * edita) ou que foram mexidos no diálogo, e só de licença que ficou. Licença sem vínculo aqui fica
 * com o padrão do backend, e vínculo de outro cliente não é mandado: o identity o preserva, e
 * mandá-lo seria 400. Sem validade, tira o prazo.
 */
export const vinculosParaPayload = (licenses = [], vinculos = []) => {
    const licencas = new Set(licenses.map(idDaLicenca).filter(Boolean));
    return vinculos
        .filter((vinculo) => licencas.has(vinculo.licenseId))
        .map((vinculo) => ({
            licenseId: vinculo.licenseId,
            papel: vinculo.papel,
            situacao: vinculo.situacao,
            validoAte: vinculo.validoAte || null,
            semValidade: !vinculo.validoAte,
        }));
};

const idsDosPerfis = (roles = []) => (roles || []).map((item) => item?.roleId || item?.id).filter(Boolean);

/**
 * Perfis, licenças e vínculos do envio. ADMIN e SUPPORT vão sem licença e sem vínculo (o identity
 * recusa equipe com vínculo) e só com os perfis que a conta já tinha como equipe, que são o grupo de
 * escrita do SUPPORT. Os de uma licença escolhida antes de trocar o tipo não vão.
 *
 * @param original a conta como foi lida, na edição; nulo na criação
 */
export const acessoParaPayload = (form, original = null) => {
    if (ehEquipeBusifarm(form?.specialAccessType)) {
        return {
            roleIdList: ehEquipeBusifarm(original?.specialAccessType) ? idsDosPerfis(original.roles) : [],
            licenseIdList: [],
            vinculos: [],
        };
    }
    return {
        roleIdList: idsDosPerfis(form?.roles),
        licenseIdList: (form?.licenses || []).map(idDaLicenca).filter(Boolean),
        vinculos: vinculosParaPayload(form?.licenses, form?.vinculos),
    };
};


export const formatDateBR = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
};

export const formatPhone = (ddi, number) => {
    if (!number) return '-';
    return ddi ? `+${ddi} ${number}` : number;
};

export const getBackendMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

const normalizeRole = (item) => ({
    ...item,
    id: item?.id || item?.roleId || '',
    roleId: item?.roleId || item?.id || '',
    roleName: item?.roleName || item?.nome || '',
    description: item?.description || item?.descricao || '',
});

const normalizeLicense = (item) => ({
    ...item,
    id: item?.id || item?.licenseId || '',
    licenseId: item?.licenseId || item?.id || '',
    status: item?.status || '-',
    tenantId: item?.tenantId || item?.tenant_id || '',
    customer: item?.customer || item?.empresa || {},
    plan: item?.plan || item?.plano || {},
});

export const normalizeUserRows = (items = []) => items.map((item) => {
    const userId = item?.userId || item?.id || '';
    const enabled = typeof item?.enabled === 'boolean' ? item.enabled : item?.status !== 'INATIVO';
    const blocked = Boolean(item?.blocked);
    const licenses = Array.isArray(item?.licenses)
        ? item.licenses.map(normalizeLicense)
        : Array.isArray(item?.licencas)
            ? item.licencas.map(normalizeLicense)
            : [];
    const vinculos = Array.isArray(item?.vinculos) ? item.vinculos.map(normalizeVinculo) : [];
    const roles = Array.isArray(item?.roles)
        ? item.roles.map(normalizeRole)
        : Array.isArray(item?.perfis)
            ? item.perfis.map(normalizeRole)
            : [];
    // Portal do cliente (BBF-179): sem o acesso, as visões vêm vazias.
    const portal = portalDaConta(item);

    return {
        ...item,
        userId,
        id: item?.id || userId,
        username: item?.username || item?.login || '',
        email: item?.email || '',
        ddiPhoneNumber: item?.ddiPhoneNumber || item?.ddi_phone_number || item?.ddi || '',
        mobilePhoneNumber: item?.mobilePhoneNumber || item?.mobile_phone_number || item?.telefone || '',
        enabled,
        blocked,
        mustRenewPassword: Boolean(item?.mustRenewPassword || item?.must_renew_password),
        expirationDate: item?.expirationDate || item?.expiration_date || null,
        specialAccessType: item?.specialAccessType || item?.accessType || item?.tipo_acesso || 'USER',
        roles,
        licenses,
        vinculos,
        acessoPortal: portal.acesso,
        visoesPortal: portal.visoes,
        createdAt: item?.createdAt || item?.criado_em || null,
        updatedAt: item?.updatedAt || item?.atualizado_em || null,
        expirationDateFmt: formatDateBR(item?.expirationDate || item?.expiration_date),
        createdAtFmt: formatDateBR(item?.createdAt || item?.criado_em),
        updatedAtFmt: formatDateBR(item?.updatedAt || item?.atualizado_em),
        phoneFmt: formatPhone(
            item?.ddiPhoneNumber || item?.ddi_phone_number || item?.ddi || '',
            item?.mobilePhoneNumber || item?.mobile_phone_number || item?.telefone || ''
        ),
    };
});

/** Recurso do identity que lista usuários: quem o tem num perfil vê a gestão de usuários. */
export const RECURSO_LISTAR_USUARIOS = 'GET:/auth/users';

/** Lê a resposta de `/auth/users/me/permissions`. */
export const podeVerUsuarios = (permissoes) => Boolean(
    permissoes?.fullAccess
    || permissoes?.readAccess
    || (Array.isArray(permissoes?.resources) && permissoes.resources.includes(RECURSO_LISTAR_USUARIOS))
);

export const getAccessTypeColor = (type) => {
    if (type === 'ADMIN') return 'success';
    if (type === 'SUPPORT') return 'info';
    if (type === 'USER') return 'warning';
    return 'default';
};

export const getUserStatusColor = (user) => {
    if (user?.blocked) return 'error';
    if (user?.enabled) return 'success';
    return 'default';
};

export const getUserStatusLabel = (user) => {
    if (user?.blocked) return 'Bloqueado';
    if (user?.enabled) return 'Ativo';
    return 'Inativo';
};

export const buildUserStats = (rows = []) => ({
    total: rows.length,
    active: rows.filter((item) => item.enabled && !item.blocked).length,
    blocked: rows.filter((item) => item.blocked).length,
    admins: rows.filter((item) => item.specialAccessType === 'ADMIN').length,
    support: rows.filter((item) => item.specialAccessType === 'SUPPORT').length,
    renewPassword: rows.filter((item) => item.mustRenewPassword).length,
});
